// Tuval zoom + pan yönetimi.
//  • Serbest zoom (preset şart değil), Ctrl+scroll ile imleç-merkezli yakınlaşma.
//  • Ekrana sığdır / %100.
//  • Space basılıyken veya orta tık ile pan (scroll'u sürükle).
// Zoom doğrudan px boyutuna gömülür (mmToPx zoom'u çarpar) — react-rnd `scale` prop'u KULLANILMAZ.
import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { PX_PER_MM, clamp } from './geometry';

const MIN = 0.1;
const MAX = 4;

export function useCanvasZoom(wrapRef, pageWmm, pageHmm) {
  const [zoom, setZoomRaw] = useState(1);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [panning, setPanning] = useState(false);
  // Ctrl+scroll sonrası scrollLeft/Top'u imleç sabit kalacak şekilde düzeltmek için.
  const pendingScroll = useRef(null);
  const panStart = useRef(null);

  const setZoom = useCallback((z) => setZoomRaw(clamp(typeof z === 'function' ? z(0) : z, MIN, MAX)), []);

  // Ctrl + tekerlek: imleç-merkezli zoom.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return; // normal scroll'a dokunma
      e.preventDefault();
      const rect = wrap.getBoundingClientRect();
      const cursorX = e.clientX - rect.left + wrap.scrollLeft;
      const cursorY = e.clientY - rect.top + wrap.scrollTop;
      setZoomRaw((prev) => {
        const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
        const next = clamp(prev * factor, MIN, MAX);
        if (next === prev) return prev;
        // İçerik koordinatı sabit kalsın: contentX = cursor / zoom → yeni scroll = contentX*next - (cursor-scroll)
        const ratio = next / prev;
        pendingScroll.current = {
          left: cursorX * ratio - (e.clientX - rect.left),
          top: cursorY * ratio - (e.clientY - rect.top),
        };
        return next;
      });
    };
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, [wrapRef]);

  // Zoom değişince bekleyen scroll düzeltmesini uygula.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (wrap && pendingScroll.current) {
      wrap.scrollLeft = pendingScroll.current.left;
      wrap.scrollTop = pendingScroll.current.top;
      pendingScroll.current = null;
    }
  }, [zoom, wrapRef]);

  // Space tuşu pan modu (INPUT/SELECT içinde değilken).
  useEffect(() => {
    const tagIsField = () => {
      const t = document.activeElement?.tagName;
      return t === 'INPUT' || t === 'SELECT' || t === 'TEXTAREA';
    };
    const down = (e) => { if (e.code === 'Space' && !tagIsField()) { e.preventDefault(); setSpaceHeld(true); } };
    const up = (e) => { if (e.code === 'Space') setSpaceHeld(false); };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  const fitToScreen = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap || !pageWmm || !pageHmm) return;
    const padding = 80; // canvasInner padding payı
    const availW = wrap.clientWidth - padding;
    const availH = wrap.clientHeight - padding;
    const z = Math.min(availW / (pageWmm * PX_PER_MM), availH / (pageHmm * PX_PER_MM));
    setZoom(clamp(z, MIN, MAX));
  }, [wrapRef, pageWmm, pageHmm, setZoom]);

  const zoomTo100 = useCallback(() => setZoom(1), [setZoom]);

  // Pan: orta tık her zaman, sol tık space basılıyken.
  const onPanMouseDown = useCallback((e) => {
    const wrap = wrapRef.current;
    if (!wrap) return false;
    const wantPan = e.button === 1 || (e.button === 0 && spaceHeld);
    if (!wantPan) return false;
    e.preventDefault();
    setPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, left: wrap.scrollLeft, top: wrap.scrollTop };
    const move = (ev) => {
      if (!panStart.current) return;
      wrap.scrollLeft = panStart.current.left - (ev.clientX - panStart.current.x);
      wrap.scrollTop = panStart.current.top - (ev.clientY - panStart.current.y);
    };
    const end = () => {
      setPanning(false); panStart.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    return true;
  }, [wrapRef, spaceHeld]);

  return { zoom, setZoom, fitToScreen, zoomTo100, spaceHeld, panning, onPanMouseDown };
}
